import { Injectable } from "@angular/core";
import { HttpClient, HttpParams } from "@angular/common/http";
import { API_URL } from "../config/general.config";
import { map, Observable, switchMap, take } from "rxjs";
import { FailureDescription, LatitudeLongitude, SpanFailureDescription, SpanFailuresRequest, TowerFailureDescription, TowerFailuresRequest } from "../modules/go/model/form.model";
import { OfflineService } from "./offline.service";
import { ConnectionStatusService } from "./connection-status.service";
import { DateISO } from "../model/general.model";

@Injectable({
  providedIn: 'root',
})
export class FailureService {

  private failureApi = API_URL + '/failures';

  constructor(
    private http: HttpClient,
    private offlineService: OfflineService,
    private connectionStatusService: ConnectionStatusService
  ) {
  }


  getSpanFailures(latitude: number, longitude: number, codes: string[], fromDate: string, toDate: string, excludeTaskId?: number): Observable<FailureDescription[]> {
    return this.connectionStatusService.networkStatus$.pipe(
      take(1),
      switchMap(status => status
        ? this.getOnlineSpanFailures(latitude, longitude, codes, fromDate, toDate, excludeTaskId)
        : this.getOfflineSpanFailures(latitude, longitude, codes, fromDate, toDate, excludeTaskId)
      )
    )
  }
  private getOfflineSpanFailures(latitude: number, longitude: number, codes: string[], fromDate: string, toDate?: string | null, excludeTaskId?: number): Observable<FailureDescription[]> {
    return this.offlineService.getSpanFailures(latitude, longitude, codes, fromDate, toDate).pipe(
      map(failures => this.excludeTaskFailures(failures, excludeTaskId))
    );
  }

  getOnlineSpanFailures(latitude: number, longitude: number, codes: string[], fromDate: string, toDate: string, excludeTaskId?: number): Observable<FailureDescription[]> {
    let params = new HttpParams()
      .set('latitude', String(latitude))
      .set('longitude', String(longitude))
      .set('fromDate', fromDate)
      .set('toDate', toDate);

    params = this.appendCodes(params, codes);
    if (excludeTaskId !== undefined && excludeTaskId !== null) {
      params = params.set('excludeTaskId', String(excludeTaskId));
    }

    return this.http.get<FailureDescription[]>(this.failureApi + '/span', { params });
  }

  getTowerFailures(latitude: number, longitude: number, codes: string[], fromDate: string, toDate: string, excludeTaskId?: number): Observable<FailureDescription[]> {
    return this.connectionStatusService.networkStatus$.pipe(
      take(1),
      switchMap(status => status
        ? this.getOnlineTowerFailures(latitude, longitude, codes, fromDate, toDate, excludeTaskId)
        : this.getOfflineTowerFailures(latitude, longitude, codes, fromDate, toDate, excludeTaskId)
      )
    )
  }
  getOnlineTowerFailures(latitude: number, longitude: number, codes: string[], fromDate: string, toDate: string, excludeTaskId?: number): Observable<FailureDescription[]> {
    let params = new HttpParams()
      .set('latitude', String(latitude))
      .set('longitude', String(longitude))
      .set('fromDate', fromDate)
      .set('toDate', toDate);

    params = this.appendCodes(params, codes);
    if (excludeTaskId !== undefined && excludeTaskId !== null) {
      params = params.set('excludeTaskId', String(excludeTaskId));
    }

    return this.http.get<FailureDescription[]>(this.failureApi + '/tower', { params });
  }

  private getOfflineTowerFailures(latitude: number, longitude: number, codes: string[], fromDate: string, toDate?: string | null, excludeTaskId?: number): Observable<FailureDescription[]> {
    return this.offlineService.getTowerFailures(latitude, longitude, codes, fromDate, toDate).pipe(
      map(failures => this.excludeTaskFailures(failures, excludeTaskId))
    );
  }

  private appendCodes(params: HttpParams, codes: string[]): HttpParams {
    codes.forEach(code => {
      params = params.append('code', code);
    });
    return params;
  }

  private excludeTaskFailures(failures: FailureDescription[], excludeTaskId?: number): FailureDescription[] {
    if (excludeTaskId === undefined || excludeTaskId === null) {
      return failures;
    }

    return failures.filter(failure => (failure as any).taskId !== excludeTaskId);
  }

  cacheHistoricalTowerFailures(locations: LatitudeLongitude[], fromDate: DateISO) {
    const body: TowerFailuresRequest = {
      fromDate,
      locations
    }
    return this.http.post<TowerFailureDescription[]>(this.failureApi + '/tower', body).pipe(
      switchMap(failures => this.offlineService.saveFailures(failures))
    )
  }

  cacheHistoricalSpanFailures(locations: LatitudeLongitude[], fromDate: string) {
    const body: SpanFailuresRequest = {
      fromDate,
      locations
    }
    return this.http.post<SpanFailureDescription[]>(this.failureApi + '/span', body).pipe(
      switchMap(failures => this.offlineService.saveFailures(failures))
    )
  }
}
